<template>
  <div class="w-100">
    <proa-card v-if="paymentSystem" class="h-100 d-flex flex-column" no-padding>
      <template v-slot:header>
        <div class="d-flex justify-content-between align-items-center">
          <proa-back-button
            @onClick="onClickBackButton"
          />
          <proa-heading detail>{{ $t('pages.management.basicInformation.paymentMethodRegistration') }}</proa-heading>
          <div/>
        </div>
      </template>
      <div class="payment-system-content">
        <table :class="['table-attributes', { 'invalid': invalidPaymentSystem }]">
          <tbody>
            <template>
              <tr v-if="invalidPaymentSystem" class="text-center">
                <th colspan="2" class="text-error text-pre-wrap">{{ $t('pages.indexManagementPaymentMethod.invalidPaymentMethod') }}</th>
              </tr>
              <tr>
                <th>{{ $t('pages.indexManagementPaymentMethod.paymentMethod') }}</th>
                <td>{{ $t('pages.indexManagementPaymentMethod.creditCard') }}</td>
              </tr>
              <tr>
                <th>{{ $t('pages.indexManagementPaymentMethod.creditCard') }}</th>
                <td>
                  <img class="mr-2 cc-image" :src="`/iframe_resources/payment_method/cc_${paymentSystem.cardType.toLowerCase()}.png`">
                  <span class="mr-2">{{ paymentSystem.cardType }}</span>
                  <span class="mr-2">{{ $t('pages.indexManagementPaymentMethod.creditCardLastDigits') }}</span>
                  <span class="mr-2">{{ paymentSystem.lastDigits }}</span>
                  <span>{{ $t('pages.indexManagementPaymentMethod.expirationDate') }}</span>
                  <span>{{ paymentSystem.validityMonth }}/{{ paymentSystem.validityYear }}</span>
                </td>
              </tr>
              <tr>
                <th>{{ $t('pages.indexManagementPaymentMethod.creditCardHolder') }}</th>
                <td>{{ paymentSystem.holderFirstName }} {{ paymentSystem.holderLastName }}</td>
              </tr>
              <tr>
                <th>{{ $t('pages.indexManagementPaymentMethod.paymentEmail') }}</th>
                <td>{{ paymentSystem.paymentEmail }}</td>
              </tr>
              <tr>
                <th>{{ $t('pages.indexManagementPaymentMethod.paymentPhoneNumber') }}</th>
                <td>{{ paymentSystem.paymentPhone }}</td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
      <div class="payment-system-card-footer">
        <proa-button
          class="default-button-width"
          @click.native="editPaymentMethodModalWithObject = paymentSystem"
        >
          <span>{{ $t('common.change') }}</span>
        </proa-button>
      </div>
    </proa-card>
    <edit-payment-method-modal
      :modal="editPaymentMethodModalWithObject != null"
      :paymentSystem="editPaymentMethodModalWithObject"
      :paymentSystemMaster="paymentSystemMaster"
      :requesting="updatePaymentMethodRequesting"
      :title="$t('pages.indexManagementPaymentMethod.creditCardRegistration')"
      @close="editPaymentMethodModalWithObject = null"
      @createdPaymentToken="onCreatedPaymentToken"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import PaymentSystem from '@/store/models/admin/PaymentSystem'
import PaymentSystemMaster from '@/store/models/admin/PaymentSystemMaster'
import EditPaymentMethodModal from '@/components/molecules/management/paymentMethod/EditModal'

export default {
  name: 'IndexManagementPaymentMethod',
  components: {
    EditPaymentMethodModal
  },
  computed: {
    invalidPaymentSystem: function() {
      return this.paymentSystem.status != 'usePay'
    },
    paymentSystem: function() {
      return PaymentSystem.query().first()
    },
    paymentSystemMaster: function() {
      return PaymentSystemMaster.query().first()
    },
    ...mapGetters({
      currentLicensedPlan: 'auth/currentLicensedPlan'
    })
  },
  data() {
    return {
      editPaymentMethodModalWithObject: null,
      updatePaymentMethodRequesting: false
    }
  },
  methods: {
    onClickBackButton: function() {
      this.$router.push({ name: 'management' })
    },
    async onCreatedPaymentToken(params) {
      this.updatePaymentMethodRequesting = true
      await this.apiPutPaymentSystem({
        id: this.paymentSystem.id,
        attributes: params
      })

      if (!this.httpError) {
        this.updatePaymentMethodRequesting = false
        this.editPaymentMethodModalWithObject = null

        // TODO: カード情報更新通知モーダル
      }
    },
    ...mapActions({
      apiGetPaymentSystems: 'entities/paymentSystems/index',
      apiGetPaymentSystemMasters: 'entities/paymentSystemMasters/index',
      apiGetSystemManage: 'systemManage/show',
      apiPutPaymentSystem: 'entities/paymentSystems/update'
    })
  },
  async created() {
    if (this.currentLicensedPlan.contractType === 'free') {
      this.$router.push({ name: 'management' })
      return
    } else {
      const isMaintenaceMode = await this.apiGetSystemManage()
      if (isMaintenaceMode) {
        this.$router.push({ name: 'management' })
        return
      }
    }
    await this.apiGetPaymentSystemMasters()
    this.apiGetPaymentSystems()
  }
}
</script>

<style scoped lang="scss">
@import "@/styles/variables";
@import "@/styles/mediaquery";

.payment-system-content {
  flex-grow: 1;
  height: 100%;
  display: flex;
  flex-direction: column;

  .table-attributes {
    width: 100%;

    &.invalid tr:not(:first-child) {
      opacity: 0.5;
    }

    tr {
      display: block;
      border-bottom: 1px solid $light-border-color;
      padding: 15px 20px;

      @include mq(md) {
        display: table-row;
        border-bottom: 1px solid $dark-border-color;
        padding: 0;
      }
    }

    th {
      display: block;
      width: 100%;
      padding: 0;
      font-weight: 600;

      @include mq(md) {
        display: table-cell;
        width: 1px;
        white-space: nowrap;
        padding: 20px 50px 20px 20px;
      }
    }

    td {
      display: block;
      width: 100%;
      padding: 0;
      word-break: break-all;

      @include mq(md) {
        display: table-cell;
        width: auto;
        padding: 20px 20px 20px 0;
      }

      .cc-image {
        height: 16px;
      }
    }
  }
}

.payment-system-card-footer {
  display: flex;
  justify-content: flex-end;
  padding: 20px;
  border-top: 1px solid $dark-border-color;
}
</style>
